import { useCallback, useEffect, useRef, useState } from 'react'
import { installEditorFontRetry } from '@/utils/editor-text-box/editorTextBoxAdapter'
import { fontLoadWarningSession } from '@/utils/editor/fontLoadWarningSession'

type FontLoadWarning = {
  show: boolean
  families: string[]
  close: () => void
}

/**
 * Lets this page's text boxes load a failed font once more
 * (installEditorFontRetry) and warns when that retry fails too. The first
 * family to fail for good opens the warning, unless this browser session has
 * already shown it. A family that fails while the warning is open joins its
 * list, once, in the order the failures arrive. Once the warning closes, or
 * when the session had already shown it, later failures open nothing.
 */
export const useFontLoadWarning = (): FontLoadWarning => {
  const [show, setShow] = useState(false)
  const [families, setFamilies] = useState<string[]>([])
  // 'waiting' until the first failure, 'open' while the warning shows, and
  // 'done' once it has closed or the session had already shown it.
  const phase = useRef<'waiting' | 'open' | 'done'>('waiting')

  useEffect(() => {
    const onFailedForGood = (family: string) => {
      if (phase.current === 'done') return
      if (phase.current === 'waiting') {
        if (fontLoadWarningSession.shown()) {
          phase.current = 'done'
          return
        }
        fontLoadWarningSession.markShown()
        phase.current = 'open'
        setShow(true)
      }
      setFamilies((listed) =>
        listed.includes(family) ? listed : [...listed, family],
      )
    }
    return installEditorFontRetry(document, onFailedForGood)
  }, [])

  const close = useCallback(() => {
    if (phase.current === 'open') phase.current = 'done'
    setShow(false)
  }, [])

  return { show, families, close }
}
